/**
 * Bank details (IBAN / BIC) helpers: normalization and validation.
 *
 * Pure, UI-agnostic functions — `validateIban`/`validateBic` return a stable
 * error code and the caller maps it to a localized message. The rules mirror
 * the backend `UtilityHelper` (generic_iban_valid? / valid_bic?): syntactic
 * format plus the IBAN MOD-97 checksum (ISO 13616), and the business
 * SEPA-zone restriction — all IBAN/BIC validation lives in this module.
 */

/** Validation outcome for an IBAN, or `null` when it is valid. */
export type IbanError = 'required' | 'format' | 'checksum' | 'sepa'

/** Validation outcome for a BIC/SWIFT code, or `null` when it is valid. */
export type BicError = 'required' | 'format'

/** ISO 3166-1 alpha-2 codes of the SEPA zone (EU + EEA + UK + CH + microstates).
 *  Frozen on purpose: the zone changes rarely, extend it manually if needed. */
const SEPA_COUNTRIES = new Set([
  'AD','AT','BE','BG','CH','CY','CZ','DE','DK','EE','ES','FI','FR','GB','GI',
  'GR','HR','HU','IE','IS','IT','LI','LT','LU','LV','MC','MT','NL','NO','PL',
  'PT','RO','SE','SI','SK','SM','VA',
])

/** Normalize IBAN/BIC input: strip whitespace and uppercase, for both
 *  validation and persistence (canonical form). */
export function normalizeBank(raw: string): string {
  return raw.replace(/\s+/g, '').toUpperCase()
}

/** MOD-97 over a numeric string, computed digit by digit so arbitrarily long
 *  IBANs never overflow JS number precision. */
function mod97(numeric: string): number {
  let remainder = 0
  for (let i = 0; i < numeric.length; i++) {
    remainder = (remainder * 10 + (numeric.charCodeAt(i) - 48)) % 97
  }
  return remainder
}

/**
 * Validate an IBAN per the backend `generic_iban_valid?`: 5-34 alphanumeric
 * characters, then the MOD-97 check (move the first 4 chars to the end, map
 * A-Z to 10-35, the result modulo 97 must equal 1).
 */
export function validateIban(raw: string): IbanError | null {
  const iban = normalizeBank(raw)
  if (!iban) return 'required'
  if (!/^[A-Z0-9]{5,34}$/.test(iban)) return 'format'
  // Move the country code + check digits to the end, then expand letters to
  // their numeric value (A=10 … Z=35) before the MOD-97 test.
  const rearranged = iban.slice(4) + iban.slice(0, 4)
  let numeric = ''
  for (let i = 0; i < rearranged.length; i++) {
    const ch = rearranged[i]
    numeric += ch >= 'A' && ch <= 'Z' ? String(ch.charCodeAt(0) - 55) : ch
  }
  if (mod97(numeric) !== 1) return 'checksum'
  // Business rule: SEPA mandates require an IBAN domiciled in the SEPA zone.
  if (!SEPA_COUNTRIES.has(iban.slice(0, 2))) return 'sepa'
  return null
}

/**
 * Validate a BIC/SWIFT code per the backend `valid_bic?` (ISO 9362): 4 letters
 * (bank) + 2 letters (country) + 2 alphanumeric (location) + optional 3
 * alphanumeric (branch) — i.e. a length of 8 or 11.
 */
export function validateBic(raw: string): BicError | null {
  const bic = normalizeBank(raw)
  if (!bic) return 'required'
  if (!/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(bic)) return 'format'
  return null
}
